import type {GuideState} from "@khan/guide-types";

interface GuideStateAction<K extends keyof GuideState> {
    /**
     * The property to be set in the state.
     */
    key: K;
    /**
     * The new value for the state value identified by `key`.
     *
     * This can be a value or a function that takes the current value
     * and returns a new value.
     *
     * NOTE(somewhatabstract): This currently just supports the current value of
     * the specified key as the argument to the function. If we need to support
     * generating a new value based off the entire state, we can add that as an
     * additional argument to that function (adding it as a new argument rather
     * than replacing the existing argument is a non-breaking change and easier
     * to implement).
     */
    value: GuideState[K] | ((arg1: GuideState[K]) => GuideState[K]);
}

/**
 * Reduce the guide state based on the action.
 */
export const guideStateReducer = <K extends keyof GuideState>(
    state: GuideState,
    {key, value}: GuideStateAction<K>,
): GuideState => {
    const currentValue = state[key];
    const realValue = typeof value === "function" ? value(currentValue) : value;
    if (realValue === currentValue) {
        // Value didn't change, so we can skip the update.
        return state;
    }
    if (
        Array.isArray(realValue) &&
        Array.isArray(currentValue) &&
        realValue.length === 0 &&
        currentValue.length === 0
    ) {
        // Empty arrays are considered equal, so we can skip the update.
        return state;
    }
    return Object.freeze({...state, [key]: realValue});
};
